import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { CircularProgress } from "@mui/material";
import { UcatLayout } from "../../Components/Shared";
import { useAppDispatch, useAppSelector } from "../../../../Redux/Store/Hooks";
import { listReExamPracticeID, clearListReExamPracticeID } from "../../../../Redux/Slices/ReExamPractice/ListReExamPracticeSlice";
import { listPracticeExamID, clearListPracticeExamID } from "../../../../Redux/Slices/PracticeExam/ListPracticeExamIDSlice";
import { addExamAnswerPractice } from "../../../../Redux/Slices/ExamAnswerPractice/AddExamAnswerPracticeSlice";
import { listExamAnswerPractice } from "../../../../Redux/Slices/ExamAnswerPractice/ListExamAnswerPracticeSlice";
import { listExamsubmitPractice } from "../../../../Redux/Slices/ExamAnswerPractice/ListExamsubmitPracticeSlice";
import { addFalg } from "../../../../Redux/Slices/Falg/AddFalgSlice";
import { listFlag } from "../../../../Redux/Slices/Falg/ListFlagSlice";
import FlagOutlinedIcon from "@mui/icons-material/FlagOutlined";
import {
    SplitPane,
    SinglePageQuestion,
    NavigatorModal,
    ScrollWarningModal,
} from "../../Components/Practice_Test/Exam_component";
import { AnswerType, Question, ExamSection, UserAnswer } from "../../Components/sample data/Type";
import "../../ucat-styles.css";
import { Flag } from "lucide-react";
import useNavigationBlocker from "../../../../Hooks/useNavigationBlocker";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

// --- Types ---


export const UcatRetakeTestScreen: React.FC = () => {
    dayjs.extend(utc);
    dayjs.extend(timezone);

    const offsetMinutes = dayjs().tz(dayjs.tz.guess()).utcOffset();
    const navigate = useNavigate();
    const location = useLocation();
    useNavigationBlocker(true);
    const dispatch = useAppDispatch();
    const { state } = location;

    // Persist state to handle reloads
    useEffect(() => {
        if (state && typeof state === 'object') {
            sessionStorage.setItem("ucat_retake_state", JSON.stringify(state));
        }
    }, [state]);

    const persistedStateStr = sessionStorage.getItem("ucat_retake_state");
    const persistedState = persistedStateStr ? JSON.parse(persistedStateStr) : null;
    const activeState = (state && typeof state === 'object') ? state : persistedState;

    // For retake, we usually have retake_id or student_test_id. Logic might vary.
    // Based on UcatRetakeTest.tsx, it uses retake_id or student_test_id.
    const student_test_id = activeState?.retake_id || activeState?.student_test_id || activeState?.exam_id;

    const { data: listReExamPracticeIDSlice, isLoading: isReExamLoading } = useAppSelector(
        (state) => state.ListReExamPracticeIDSlice
    );

    const { data: listExamAnswerSlice } = useAppSelector(
        (state) => state.ListExamAnswerPracticeSlice
    );

    const { data: listFlagSlice } = useAppSelector(
        (state) => state.ListFlagSlice
    );

    const { data: addExamAnswerRes } = useAppSelector(
        (state) => state.AddExamAnswerPracticeSlice
    );

    const { data: addFlagRes } = useAppSelector(
        (state) => state.AddFalgSlice
    );

    const { data: listPracticeExamIDData, isLoading: isStartExamLoading } = useAppSelector(
        (state) => state.ListPracticeExamIDSlice
    );

    const isDataLoading = isReExamLoading || isStartExamLoading;

    // --- Local State ---
    const [questions, setQuestions] = useState<Question[]>([]);
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
    const [selectedAnswers, setSelectedAnswers] = useState<{ [questionId: number]: number }>({});
    const [dragDropAnswers, setDragDropAnswers] = useState<{ [questionId: number]: Record<number, string | null> }>({});
    const [timeLeft, setTimeLeft] = useState<number>(0);
    const [examName, setExamName] = useState("UCAT Practice Test");
    const [isExamLoaded, setIsExamLoaded] = useState(false);
    const [markedQuestions, setMarkedQuestions] = useState<{ [index: number]: boolean }>({});
    const [scrollStatus, setScrollStatus] = useState({ left: true, right: true });
    const [isNavigatorOpen, setIsNavigatorOpen] = useState(false);
    const [questionStartTime, setQuestionStartTime] = useState<string>(new Date().toISOString());
    const [showScrollWarning, setShowScrollWarning] = useState(false);
    const [newStudentTestId, setNewStudentTestId] = useState<number | null>(null);
    const [lastSeenSubtypeId, setLastSeenSubtypeId] = useState<number | null>(activeState?.subtype_id ?? null);
    const [viewedQuestions, setViewedQuestions] = useState<{ [questionId: number]: boolean }>({});

    useEffect(() => {
        if (questions.length > 0 && questions[currentQuestionIndex]) {
            const qId = questions[currentQuestionIndex].question_id;
            if (!viewedQuestions[qId]) {
                setViewedQuestions(prev => ({ ...prev, [qId]: true }));
            }
        }
    }, [currentQuestionIndex, questions, viewedQuestions]);

    useEffect(() => {
        setQuestionStartTime(new Date().toISOString());
    }, [currentQuestionIndex]);

    // --- Helpers ---
    const convertToSeconds = (timeStr: string): number => {
        if (!timeStr) return 0;
        if (timeStr.includes("minute")) {
            return parseInt(timeStr) * 60;
        }
        return parseInt(timeStr) || 0;
    };

    const formatTime = (seconds: number) => {
        const m = Math.floor(seconds / 60);
        const s = seconds % 60;
        return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
    };

    // --- 1. Fetch Data ---
    useEffect(() => {
        if (student_test_id) {
            const id = Number(student_test_id);
            dispatch(listReExamPracticeID({ student_test_id: id, client_tz_offset: offsetMinutes }));
            dispatch(listExamAnswerPractice({ student_test_id: id }));
            dispatch(listFlag({ student_test_id: id }));
        }
    }, [dispatch, student_test_id]);

    // Re-fetch when answer or flag is added
    useEffect(() => {
        if (student_test_id) {
            dispatch(listExamAnswerPractice({ student_test_id: Number(student_test_id) }));
        }
    }, [dispatch, student_test_id, addExamAnswerRes]);

    useEffect(() => {
        if (student_test_id) {
            dispatch(listFlag({ student_test_id: Number(student_test_id) }));
        }
    }, [dispatch, student_test_id, addFlagRes]);

    // --- 2. Trigger Start Test API after Retake Success ---
    useEffect(() => {
        const rawReExamData: any = (listReExamPracticeIDSlice as any)?.data || listReExamPracticeIDSlice;
        const reExamData = Array.isArray(rawReExamData) ? rawReExamData[0] : rawReExamData;

        if (reExamData?.id || reExamData?.student_test_id) {
            const newId = reExamData.id || reExamData.student_test_id;
            if (newId !== newStudentTestId) {
                setNewStudentTestId(newId);
                dispatch(listPracticeExamID({ student_test_id: Number(newId) }));
            }
        }
    }, [listReExamPracticeIDSlice, dispatch, newStudentTestId]);

    // --- 3. Normalize Data from Start Test API ---
    useEffect(() => {
        const rawData: any = (listPracticeExamIDData as any)?.data || listPracticeExamIDData;
        const data = Array.isArray(rawData) ? rawData[0] : rawData;

        if (data && data.test_name) { // Using test_name as a sign that data is loaded
            console.log("UcatRetakeTest Normalized Start Test Data:", data);

            setExamName(data.test_name || data.exam_name || "UCAT Practice Test");

            let allQuestions: Question[] = [];
            let sections = data.question_by_type || data.questions_by_type || [];

            // Handle flat questions array if sections structure is missing
            if (sections.length === 0 && data.questions && Array.isArray(data.questions)) {
                sections = [{
                    question_type_name: data.test_name || "Practice Test",
                    question_type_id: 0,
                    question_count: data.questions.length,
                    questions: data.questions,
                    total_timing: data.total_timing || data.duration
                }];
            }

            // Timer Setup
            if (!isExamLoaded && sections.length > 0) {
                let timeStr = data.pending_time || sections[0].total_timing || sections[0].duration || "1 minutes";

                // For retake specifically, usually the full duration
                if (timeStr === "00:00:00" || timeStr === "0") {
                    timeStr = sections[0].total_timing || sections[0].duration || "1 minutes";
                }

                let seconds = 0;
                const str = String(timeStr);
                if (str.includes(":")) {
                    const parts = str.split(":").map(Number);
                    if (parts.length === 3) {
                        seconds = (parts[0] * 3600) + (parts[1] * 60) + parts[2];
                    } else if (parts.length === 2) {
                        seconds = (parts[0] * 60) + (parts[1]);
                    }
                } else {
                    seconds = convertToSeconds(str);
                }

                if (seconds <= 0) seconds = 60;
                setTimeLeft(seconds);
            }

            const initialSelectedAnswers: { [questionId: number]: number } = {};
            const initialMarkedQuestions: { [index: number]: boolean } = {};
            let globalIdx = 0;

            sections.forEach((section: any) => {
                if (section.questions) {
                    const mapped = section.questions.map((q: any): Question => {
                        const qId = q.question_id || q.id;
                        const chosenOption = (q.options || []).find((opt: any) => opt.chosen === true);
                        if (chosenOption) {
                            initialSelectedAnswers[qId] = chosenOption.option_id || chosenOption.id;
                        }

                        if (q.is_flagged || q.flag_status) {
                            initialMarkedQuestions[globalIdx] = true;
                        }

                        globalIdx++;

                        return {
                            question_id: qId,
                            question_type_id: q.question_type_id || section.question_type_id || 0,
                            question_type_name: q.question_type_name || section.question_type_name || "Multiple Choice",
                            question_text: q.question_text || q.question || "",
                            instruction: q.instruction || q.passage || null,
                            prompt: q.prompt || null,
                            prompt_url: q.prompt_url || null,
                            question_url: q.question_url || q.image_url || null,
                            imgWidth: q.imgWidth || 0,
                            prompt_imgWidth: q.prompt_imgWidth || 0,
                            source_type: q.source_type || "practice",
                            subtype_id: q.subtype_id || null,
                            subtype_name: q.subtype_name || null,
                            answer_type: AnswerType.MCQ,
                            options: (q.options || []).map((opt: any) => ({
                                option_id: opt.option_id || opt.id,
                                option_text: opt.option_text || opt.text || opt.option || "",
                                option_url: opt.option_url || opt.image_url || null,
                            })),
                        };
                    });
                    allQuestions = [...allQuestions, ...mapped];
                }
            });

            if (allQuestions.length > 0) {
                setSelectedAnswers(initialSelectedAnswers);
                setMarkedQuestions(initialMarkedQuestions);
                setQuestions(allQuestions);
                setIsExamLoaded(true);
            }
        }
    }, [listPracticeExamIDData]);

    // --- 3. Sync Answers and Flags ---
    useEffect(() => {
        const answerData = (listExamAnswerSlice?.data as any)?.data || (Array.isArray(listExamAnswerSlice?.data) ? listExamAnswerSlice?.data : []);
        if (answerData && Array.isArray(answerData)) {
            const answers: { [questionId: number]: number } = {};
            const ddAnswers: { [questionId: number]: Record<number, string | null> } = {};
            const viewed: { [questionId: number]: boolean } = {};

            answerData.forEach((ans: any) => {
                const qId = Number(ans.question_id);
                if (ans.option_id) {
                    answers[qId] = Number(ans.option_id);
                }
                // If an answer record exists, the question has been viewed/interacted with
                viewed[qId] = true;

                if (ans.answer_data) {
                    try {
                        ddAnswers[qId] = JSON.parse(ans.answer_data);
                    } catch (e) {
                        console.error("Failed to parse answer_data", ans.answer_data);
                    }
                }
            });

            setSelectedAnswers(answers);
            setDragDropAnswers(ddAnswers);
            setViewedQuestions(prev => ({ ...prev, ...viewed }));
        }
    }, [listExamAnswerSlice]);

    useEffect(() => {
        const responseObj = listFlagSlice?.data as any;
        const flagItems = responseObj?.data;
        const hasFlagsLoaded = Array.isArray(flagItems);

        if (hasFlagsLoaded && questions.length > 0) {
            const marked: { [index: number]: boolean } = {};

            flagItems.forEach((f: any) => {
                if (f.flagged) {
                    const fQId = f.question_id || f.id;
                    const qIdx = questions.findIndex(q => Number(q.question_id) === Number(fQId));
                    if (qIdx !== -1) {
                        marked[qIdx] = true;
                    }
                }
            });
            setMarkedQuestions(marked);
        }
    }, [listFlagSlice, questions]);

    // --- 3. Timer (Disabled for Retake) ---
    /*
    useEffect(() => {
        if (!isExamLoaded) return;
        const interval = setInterval(() => {
            setTimeLeft((prev) => {
                if (prev <= 1) {
                    clearInterval(interval);
                    handleFinish(); // Auto finish
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);
        return () => clearInterval(interval);
    }, [isExamLoaded]); // Removed timeLeft dependency
    */

    useEffect(() => {
        if (questions.length > 0 && isExamLoaded) {
            const currentQ = questions[currentQuestionIndex];

            // Only trigger if we actually moved to a different question OR it's the first load
            // and the subtype is different from what we last showed instructions for.
            if (currentQ.subtype_id !== lastSeenSubtypeId) {
                // Find instructions for this subtype in practiceExamData
                // For Retake, we use listPracticeExamIDData which corresponds to listPracticeExamIDSlice
                const currentSection = listPracticeExamIDData?.data?.question_by_type?.find(
                    (s: any) => s.question_type_id === currentQ.question_type_id
                );

                const subtype = (currentSection as any)?.subtype_data?.find(
                    (st: any) => (st.subtype_id === currentQ.subtype_id || st.id === currentQ.subtype_id)
                );

                // Calculate the subtype index
                const subtypeIndex = (currentSection as any)?.subtype_data?.findIndex(
                    (st: any) => (st.subtype_id === currentQ.subtype_id || st.id === currentQ.subtype_id)
                ) ?? 0;

                if (subtype && subtype.subtype_instruction && subtype.subtype_instruction.length > 0) {
                    setLastSeenSubtypeId(currentQ.subtype_id);
                    // Navigate to instructions screen instead of setting local state
                    navigate("/ucat_test_instructions", {
                        state: {
                            id: Number(student_test_id),
                            questionIndex: currentQuestionIndex,
                            subtype_id: currentQ.subtype_id,
                            subtype_instructions: subtype.subtype_instruction,
                            subtype_name: subtype.subtype_name,
                            isReturningFromSubtype: true,
                            subtype_flag: true,
                            subtype_index: subtypeIndex
                        }
                    });
                } else {
                    // No instructions, just update the seen ID so we don't check again for this subtype
                    setLastSeenSubtypeId(currentQ.subtype_id);
                }
            }
        }
    }, [currentQuestionIndex, questions, isExamLoaded, listPracticeExamIDData, lastSeenSubtypeId, student_test_id, navigate]);


    // --- 4. Actions ---
    const handleAnswerSelect = (optionIndex: number) => {
        const currentQ = questions[currentQuestionIndex];
        if (!currentQ) return;

        const selectedOption = currentQ.options[optionIndex];
        if (selectedOption) {
            setSelectedAnswers(prev => ({
                ...prev,
                [currentQ.question_id]: selectedOption.option_id
            }));
        }
    };

    const saveCurrentAnswer = () => {
        const currentQ = questions[currentQuestionIndex];
        if (!currentQ) return;

        const currentSelectedOptionId = selectedAnswers[currentQ.question_id];
        const currentTime = new Date().toISOString();

        const payload = {
            student_test_id: Number(newStudentTestId || student_test_id),
            question_id: currentQ.question_id,
            option_id: currentSelectedOptionId || null,
            remaining_time: formatTime(timeLeft),
            skipped: !currentSelectedOptionId,
            question_type_id: currentQ.question_type_id,
            question_start_time: questionStartTime,
            question_end_time: currentTime,
        };
        dispatch(addExamAnswerPractice(payload));
    };

    const handleDragDropChange = (itemId: number, bucketName: string | null) => {
        const currentQ = questions[currentQuestionIndex];
        if (!currentQ) return;

        setDragDropAnswers(prev => ({
            ...prev,
            [currentQ.question_id]: {
                ...(prev[currentQ.question_id] || {}),
                [itemId]: bucketName
            }
        }));
    };

    const handleNext = () => {
        // Check scroll status for content that requires scrolling
        if (!scrollStatus.left || !scrollStatus.right) {
            setShowScrollWarning(true);
            return;
        }

        saveCurrentAnswer();
        if (currentQuestionIndex < questions.length - 1) {
            setCurrentQuestionIndex(prev => prev + 1);
        } else {
            handleFinish();
        }
    };

    const handlePrevious = () => {
        if (currentQuestionIndex > 0) {
            setCurrentQuestionIndex(prev => prev - 1);
        }
    };

    const handleFinish = async () => {
        saveCurrentAnswer();

        // Finalize the test
        await dispatch(
            listExamsubmitPractice({
                id: Number(newStudentTestId || student_test_id),
            })
        );

        // Navigate to Result/Review screen
        navigate("/ucat_examresult_practice", {
            state: {
                studentExamId: newStudentTestId || student_test_id,
                evalAs: "test",
            }
        });
    };

    const handleToggleFlag = () => {
        const currentQ = questions[currentQuestionIndex];
        if (!currentQ) return;

        // Optimistic update
        setMarkedQuestions(prev => ({
            ...prev,
            [currentQuestionIndex]: !prev[currentQuestionIndex]
        }));

        // API call
        dispatch(
            addFalg({
                student_test_id: Number(newStudentTestId || student_test_id),
                question_id: currentQ.question_id,
            })
        );
    };

    // --- Navigator Logic ---
    const getSections = (): ExamSection[] => {
        const sections: ExamSection[] = [];
        let currentSection: ExamSection | null = null;

        questions.forEach((q) => {
            if (!currentSection || currentSection.title !== q.question_type_name) {
                if (currentSection) sections.push(currentSection);
                currentSection = {
                    id: q.question_type_id,
                    title: q.question_type_name,
                    questions: []
                };
            }
            currentSection.questions.push(q);
        });
        if (currentSection) sections.push(currentSection);
        return sections;
    };

    const sections = getSections();

    // Calculate current section and question index within that section
    let currentSectionIndex = 0;
    let questionIndexInSection = 0;
    let count = 0;
    for (let i = 0; i < sections.length; i++) {
        if (currentQuestionIndex < count + sections[i].questions.length) {
            currentSectionIndex = i;
            questionIndexInSection = currentQuestionIndex - count;
            break;
        }
        count += sections[i].questions.length;
    }

    const handleJump = (sectionIdx: number, questionIdx: number) => {
        if (!scrollStatus.left || !scrollStatus.right) {
            setIsNavigatorOpen(false);
            setShowScrollWarning(true);
            return;
        }

        let globalIndex = 0;
        for (let i = 0; i < sectionIdx; i++) {
            globalIndex += sections[i].questions.length;
        }
        globalIndex += questionIdx;
        setCurrentQuestionIndex(globalIndex);
        setIsNavigatorOpen(false);
    };

    const navigatorAnswers: Record<string, UserAnswer> = {};
    questions.forEach((q, idx) => {
        navigatorAnswers[q.question_id] = {
            selectedOptionIndex: selectedAnswers[q.question_id] !== undefined
                ? q.options.findIndex(o => o.option_id === selectedAnswers[q.question_id])
                : null,
            isFlagged: markedQuestions[idx] || false, // markedQuestions keyed by index currently
            isViewed: viewedQuestions[q.question_id] || false
        };
    });

    // --- Keyboard Shortcuts ---
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === "Escape") {
                if (showScrollWarning) {
                    setShowScrollWarning(false);
                    e.preventDefault();
                } else if (isNavigatorOpen) {
                    setIsNavigatorOpen(false);
                    e.preventDefault();
                }
            }

            if (e.altKey) {
                switch (e.key.toLowerCase()) {
                    case 'n':
                        e.preventDefault();
                        handleNext();
                        break;
                    case 'p':
                        e.preventDefault();
                        handlePrevious();
                        break;
                    case 'f':
                        e.preventDefault();
                        handleToggleFlag();
                        break;
                    case 'e':
                        e.preventDefault();
                        handleFinish();
                        break;
                    case 'v':
                        e.preventDefault();
                        setIsNavigatorOpen(true);
                        break;
                    case 'o':
                        if (showScrollWarning) {
                            e.preventDefault();
                            setShowScrollWarning(false);
                        }
                        break;
                }
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [currentQuestionIndex, questions.length, markedQuestions, showScrollWarning, isNavigatorOpen, scrollStatus]);

    // --- Render ---
    if (isDataLoading) {
        return (
            <div className="flex h-screen items-center justify-center">
                <CircularProgress />
            </div>
        );
    }

    const currentQuestion = questions[currentQuestionIndex];
    if (!currentQuestion) return <div>No Questions Available</div>;

    const currentSelectedOptionId = selectedAnswers[currentQuestion.question_id];
    const selectedOptionIndex = currentQuestion.options.findIndex(opt => opt.option_id === currentSelectedOptionId);
    const currentDragDropAnswers = dragDropAnswers[currentQuestion.question_id] || {};

    const userAnswer = {
        isFlagged: markedQuestions[currentQuestionIndex] || false,
        selectedOptionIndex: selectedOptionIndex
    };

    // Decide Layout
    const hasPassage = !!currentQuestion.prompt || !!currentQuestion.prompt_url || !!currentQuestion.instruction;

    // Standard UCAT sections that use split screen
    const isReadingOrVerbal = ["Reading Comprehension", "Verbal Reasoning", "Decision Making"].some(type =>
        currentQuestion.question_type_name.toLowerCase().includes(type.toLowerCase())
    );

    const shouldUseSplitLayout = (isReadingOrVerbal || hasPassage);

    return (
        <UcatLayout
            headerTitle={examName}
            showToolbar={true}
            headerSubtitle={`Question ${currentQuestionIndex + 1} of ${questions.length}`}
            // timerValue={formatTime(timeLeft)}
            showCalculatorButton={true}
            showClearFlagButton={true}
            onClearFlagClick={handleToggleFlag}
            footerProps={{
                onNext: handleNext,
                onPrevious: handlePrevious,
                onEndExam: handleFinish,
                onNavigator: () => setIsNavigatorOpen(true),
                showNavigator: true,
                leftButtonText: "",
                rightButtonText: currentQuestionIndex === questions.length - 1 ? "Finish" : "Next",
                previousDisabled: currentQuestionIndex === 0,
                showPrevious: true,
            }}
            toolbarRightActions={

                <button
                    onClick={handleToggleFlag}
                    className="flex items-center gap-1.5 text-white hover:text-[#FFFF00] px-3 h-full transition-all duration-200 group"
                >
                    <Flag
                        size={18}
                        className={
                            userAnswer?.isFlagged
                                ? "fill-[#FFFF00] text-[#FFFF00] drop-shadow-[0_0_8px_rgba(255,140,0,0.5)]"
                                : "text-white/90 group-hover:scale-110 transition-transform"
                        }
                    />
                    <span className="text-[16px] font-medium tracking-tight">
                        <span className="underline decoration-[1.5px] text-[16px] underline-offset-[3px]">F</span>{userAnswer.isFlagged ? "lagged" : "lag"} for Review
                    </span>
                </button>
            }
        >
            <div className="flex-1 overflow-hidden flex flex-col h-full">
                {shouldUseSplitLayout ? (
                    <SplitPane
                        passage={currentQuestion.prompt || currentQuestion.instruction || undefined}
                        promptUrl={currentQuestion.prompt_url || undefined}
                        question={currentQuestion}
                        selectedOptionIndex={selectedOptionIndex}
                        dragDropAnswers={currentDragDropAnswers}
                        onOptionSelect={handleAnswerSelect}
                        onDragDropChange={handleDragDropChange}
                        isReviewMode={false}
                        onScrollStatusChange={setScrollStatus}
                        onLockedClick={() => setShowScrollWarning(true)}
                    />
                ) : (
                    <SinglePageQuestion
                        question={currentQuestion}
                        selectedOptionIndex={selectedOptionIndex}
                        dragDropAnswers={currentDragDropAnswers}
                        passage={currentQuestion.prompt || currentQuestion.instruction || undefined}
                        promptUrl={currentQuestion.prompt_url || undefined}
                        onOptionSelect={handleAnswerSelect}
                        onDragDropChange={handleDragDropChange}
                        isReviewMode={false}
                        onScrollStatusChange={setScrollStatus}
                        onLockedClick={() => setShowScrollWarning(true)}
                    />
                )}
            </div>

            <NavigatorModal
                isOpen={isNavigatorOpen}
                onClose={() => setIsNavigatorOpen(false)}
                sections={sections}
                currentSectionIndex={currentSectionIndex}
                currentQuestionIndex={questionIndexInSection}
                answers={navigatorAnswers}
                onJump={handleJump}
            />

            <ScrollWarningModal
                isOpen={showScrollWarning}
                onClose={() => setShowScrollWarning(false)}
            />
        </UcatLayout>
    );
};

export default UcatRetakeTestScreen;
